import { UiSchema, RJSFSchema } from '@rjsf/utils';
import { UserContextModal } from 'src/auth/types/UserContextTypes';
import {
  fetchAgencyName,
  fetchCountry,
  fetchRole,
} from 'src/utilities/AuthParamHelper';

export interface CaseCreationContextParams extends UserContextModal {}

export interface SimpleValue {
  simple: string;
}

export interface ComplexValue {
  complex: string;
}

export type Value = SimpleValue | ComplexValue;
export interface CaseCreationProperties {
  label: string;
  type?: string;
  value: Value;
}
export interface CaseCreationStaticSchema {
  title?: string;
  description?: string;
  properties: {
    [attribute: string]: CaseCreationProperties;
  };
}

export interface CreateCaseSchemaDataDefinition {
  [attribute: string]:
    | CreateCaseSchemaDataDefinition
    | ((userDetails: CaseCreationContextParams) => string);
}

export const createCaseSchemaDataDefinitionModal: CreateCaseSchemaDataDefinition =
  {
    fullName: (userContext: CaseCreationContextParams) =>
      userContext.customerFullName,
    emailId: (userContext: CaseCreationContextParams) => userContext.emailId,
    customerId: (userContext: CaseCreationContextParams) =>
      userContext.customerId,
    agency: (userContext: CaseCreationContextParams) =>
      fetchAgencyName(
        userContext.preferredAccountId,
        userContext.accountAttributes,
      ),
    country: (userContext: CaseCreationContextParams) =>
      fetchCountry(
        userContext.preferredAccountId,
        userContext.accountAttributes,
      ),
    role: (userContext: CaseCreationContextParams) =>
      fetchRole(userContext.preferredAccountId, userContext.accountAttributes),
  };

//TODO: Delete it after CPR Multitenancy
export const formData: any = {
  ukProductSafetyForm: {
    removalInformation: [{}],
    recallInformation: [{}],
    informationRequest: {
      otherBoolean: false,
    },
  },
};

//TODO: Delete it after CPR Multitenancy
export const createCaseStaticSchema: CaseCreationStaticSchema = {
  title: 'Referral Information',
  properties: {
    fullName: {
      label: 'Full Name',
      value: {
        complex: 'fullName',
      },
    },
    email: {
      label: 'Email',
      value: {
        complex: 'emailId',
      },
    },
    agency: {
      label: 'Agency',
      value: {
        complex: 'agency',
      },
    },
    country: {
      label: 'Country',
      value: { complex: 'country' },
    },
    role: {
      label: 'Role',
      value: { complex: 'role' },
    },
  },
};
//TODO: Delete it after CPR Multitenancy
export const uiSchema: UiSchema = {
  caseTitle: {
    'ui:widget': 'KatalTextInputWidget',
  },
  description: {
    'ui:widget': 'KatalTextAreaWidget',
  },
  ukProductSafetyForm: {
    isProductRemovalFormRequired: {
      'ui:widget': 'KatalCheckboxWidget',
    },
    isInformationRequestFormRequired: {
      'ui:widget': 'KatalCheckboxWidget',
    },
    isProductRecallFormRequired: {
      'ui:widget': 'KatalCheckboxWidget',
    },
    isMeetingRequestFormRequired: {
      'ui:widget': 'KatalCheckboxWidget',
    },
    isOtherFormRequired: {
      'ui:widget': 'KatalCheckboxWidget',
    },
    isNoActionFormRequired: {
      'ui:widget': 'KatalCheckboxWidget',
    },
    removalInformation: {
      items: {
        issueType: {
          'ui:widget': 'KatalTextInputWidget',
        },
        effectedRegulation: {
          'ui:widget': 'KatalTextInputWidget',
        },
        impactedMarketplaces: {
          'ui:widget': 'KatalDropdownWidget',
          'ui:multiple': true,
        },
        needByDate: {
          'ui:widget': 'KatalDatePickerWidget',
        },
        explanation: {
          'ui:widget': 'KatalTextAreaWidget',
        },
        affectedProducts: {
          'ui:widget': 'KatalTextAreaWidget',
        },
        furtherProductInfo: {
          'ui:widget': 'KatalTextAreaWidget',
        },
      },
    },
    recallInformation: {
      items: {
        issueType: {
          'ui:widget': 'KatalTextInputWidget',
        },
        effectedRegulation: {
          'ui:widget': 'KatalTextInputWidget',
        },
        impactedMarketplaces: {
          'ui:widget': 'KatalDropdownWidget',
          'ui:multiple': true,
        },
        explanation: {
          'ui:widget': 'KatalTextAreaWidget',
        },
        affectedProducts: {
          'ui:widget': 'KatalTextAreaWidget',
        },
        furtherProductInfo: {
          'ui:widget': 'KatalTextAreaWidget',
        },
        needByDate: {
          'ui:widget': 'KatalDatePickerWidget',
        },
      },
    },
    otherRequest: {
      otherReason: {
        'ui:widget': 'KatalTextAreaWidget',
      },
      needByDate: {
        'ui:widget': 'KatalDatePickerWidget',
      },
    },
    meetingRequest: {
      meetingReason: {
        'ui:widget': 'KatalTextAreaWidget',
      },
      needByDate: {
        'ui:widget': 'KatalDatePickerWidget',
      },
    },
    informationRequest: {
      isTakeDownDateRequired: {
        'ui:widget': 'KatalCheckboxWidget',
      },
      isNumberOfProductSalesRequired: {
        'ui:widget': 'KatalCheckboxWidget',
      },
      isThirdPartySellerDetailsRequired: {
        'ui:widget': 'KatalCheckboxWidget',
      },
      isProductStockDetailsRequired: {
        'ui:widget': 'KatalCheckboxWidget',
      },
      isCustomerMessagingCopyRequired: {
        'ui:widget': 'KatalCheckboxWidget',
      },
      otherBoolean: {
        'ui:widget': 'KatalCheckboxWidget',
      },
      productInformation: {
        'ui:widget': 'KatalTextAreaWidget',
      },
      needByDate: {
        'ui:widget': 'KatalDatePickerWidget',
      },
      otherReason: {
        'ui:widget': 'KatalTextAreaWidget',
      },
    },
  },
};
//TODO: Delete it after CPR Multitenancy
export const createCaseSchema: RJSFSchema = {
  title: 'Request Summary',
  type: 'object',
  required: ['caseTitle', 'ukProductSafetyForm', 'description'],
  definitions: {
    productRemovalInfo: {
      type: 'array',
      title: 'Issue Information',
      minItems: 1,
      items: {
        type: 'object',
        required: [
          'issueType',
          'effectedRegulation',
          'impactedMarketplaces',
          'explanation',
          'affectedProducts',
        ],
        properties: {
          needByDate: {
            type: 'string',
            title: 'Need By Date',
          },
          issueType: {
            title: 'Issue Type',
            type: 'string',
            maxLength: 250,
          },
          effectedRegulation: {
            title: 'Relevant Regulation',
            type: 'string',
            maxLength: 250,
          },
          impactedMarketplaces: {
            title: 'Impacted Marketplaces',
            type: 'array',
            uniqueItems: true,
            items: {
              type: 'string',
              enum: ['United Kingdom (UK)', 'Europe (All countries)'],
            },
          },
          explanation: {
            title: 'Explanation',
            type: 'string',
            maxLength: 5000,
          },
          affectedProducts: {
            type: 'string',
            title: 'Affected Products (ASINs/WebURLs)',
            maxLength: 5000,
          },
          furtherProductInfo: {
            title: 'Further Product information',
            type: 'string',
            maxLength: 5000,
          },
        },
      },
    },
    productRecallInfo: {
      type: 'array',
      title: 'Issue Information',
      minItems: 1,
      items: {
        type: 'object',
        required: [
          'issueType',
          'effectedRegulation',
          'impactedMarketplaces',
          'explanation',
          'affectedProducts',
        ],
        properties: {
          needByDate: {
            type: 'string',
            title: 'Need By Date',
          },
          issueType: {
            title: 'Issue Type',
            type: 'string',
            maxLength: 250,
          },
          effectedRegulation: {
            title: 'Relevant Regulation',
            type: 'string',
            maxLength: 250,
          },
          impactedMarketplaces: {
            title: 'Impacted Marketplaces',
            type: 'array',
            uniqueItems: true,
            items: {
              type: 'string',
              enum: ['United Kingdom (UK)', 'Europe (All countries)'],
            },
          },
          explanation: {
            title: 'Explanation',
            type: 'string',
            maxLength: 5000,
          },
          affectedProducts: {
            type: 'string',
            title: 'Affected Products (ASINs/Web URLs)',
            maxLength: 5000,
          },
          furtherProductInfo: {
            title: 'Further Product information',
            type: 'string',
            maxLength: 5000,
          },
        },
      },
    },
    additionalInfo: {
      title: 'Additional Information',
      type: 'object',
      required: ['needByDate', 'productInformation'],
      properties: {
        productInformation: {
          title: 'Product Information (ASINs/Web URLs)',
          type: 'string',
          maxLength: 5000,
        },
        needByDate: {
          type: 'string',
          title: 'Need By Date',
        },
        isTakeDownDateRequired: {
          type: 'boolean',
          title: 'Date of take down of the product',
        },
        isNumberOfProductSalesRequired: {
          type: 'boolean',
          title: 'Number of product Sales',
        },
        isThirdPartySellerDetailsRequired: {
          type: 'boolean',
          title: 'Details of Third Party Sellers',
        },
        isProductStockDetailsRequired: {
          type: 'boolean',
          title: 'Information on product stocks',
        },
        isCustomerMessagingCopyRequired: {
          type: 'boolean',
          title: 'Copy of customer messaging',
        },
        otherBoolean: {
          type: 'boolean',
          title: 'OTHER – please specify',
          default: false,
        },
      },
      allOf: [
        {
          if: {
            properties: {
              otherBoolean: {
                const: true,
              },
            },
          },
          then: {
            properties: {
              otherReason: {
                title: 'Please specify details',
                type: 'string',
              },
            },
            required: ['otherReason'],
          },
        },
      ],
    },
    meetingRequest: {
      title: 'Meeting request',
      required: ['meetingReason', 'needByDate'],
      properties: {
        needByDate: {
          type: 'string',
          title: 'Need By Date',
        },
        meetingReason: {
          type: 'string',
          title: 'Please describe the key discussion points of the meeting',
          maxLength: 5000,
        },
      },
    },
    otherRequest: {
      title: 'Other',
      required: ['otherReason'],
      properties: {
        needByDate: {
          type: 'string',
          title: 'Need By Date',
        },
        otherReason: {
          title: 'Please describe your request in detail here',
          type: 'string',
          maxLength: 5000,
        },
      },
    },
  },
  properties: {
    caseTitle: {
      type: 'string',
      title: 'Request Title',
      maxLength: 250,
    },
    description: {
      type: 'string',
      title: 'Description',
      maxLength: 5000,
    },
    ukProductSafetyForm: {
      title: 'Request Type',
      properties: {
        isProductRemovalFormRequired: {
          type: 'boolean',
          title: 'Removal of product from sale',
          default: false,
        },
        isInformationRequestFormRequired: {
          type: 'boolean',
          title: 'Information request',
          default: false,
        },
        isProductRecallFormRequired: {
          type: 'boolean',
          title: 'Recall from end user',
          default: false,
        },
        isMeetingRequestFormRequired: {
          type: 'boolean',
          title: 'Meeting request',
          default: false,
        },
        isOtherFormRequired: {
          type: 'boolean',
          title: 'Other request',
          default: false,
        },
        isNoActionFormRequired: {
          type: 'boolean',
          title: 'No action needed (FYI)',
          default: false,
        },
      },
      allOf: [
        {
          if: {
            properties: {
              isProductRemovalFormRequired: {
                const: true,
              },
            },
          },
          then: {
            properties: {
              removalInformation: {
                title: 'Removal of product from sale',
                $ref: '#/definitions/productRemovalInfo',
              },
            },
            required: ['removalInformation'],
          },
        },
        {
          if: {
            properties: {
              isInformationRequestFormRequired: {
                const: true,
              },
            },
          },
          then: {
            properties: {
              informationRequest: {
                title: 'Information request',
                $ref: '#/definitions/additionalInfo',
              },
            },
            required: ['informationRequest'],
          },
        },
        {
          if: {
            properties: {
              isProductRecallFormRequired: {
                const: true,
              },
            },
          },
          then: {
            properties: {
              recallInformation: {
                title: 'Recall from end user',
                $ref: '#/definitions/productRecallInfo',
              },
            },
            required: ['recallInformation'],
          },
        },
        {
          if: {
            properties: {
              isMeetingRequestFormRequired: {
                const: true,
              },
            },
          },
          then: {
            properties: {
              meetingRequest: {
                title: 'Meeting request',
                $ref: '#/definitions/meetingRequest',
              },
            },
            required: ['meetingRequest'],
          },
        },
        {
          if: {
            properties: {
              isOtherFormRequired: {
                const: true,
              },
            },
          },
          then: {
            properties: {
              otherRequest: {
                title: 'Other Request',
                $ref: '#/definitions/otherRequest',
              },
            },
            required: ['otherRequest'],
          },
        },
      ],
    },
  },
};
